import React, { useState, useCallback, useEffect } from 'react';
import { 
  Lock, Eye as EyeIcon, EyeOff, Settings, Globe, Shield, 
  ToggleLeft, ToggleRight, AlertTriangle, CheckCircle, Save
} from 'lucide-react';
import { useTheme } from '../ThemeContext';
import './Integration.css';

const Integration = () => {
  const [config, setConfig] = useState({
    ghlEnabled: false,
    ghlApiToken: '',
    ghlLocationId: '',
    ghlPipelineId: '',
    ghlPipelineStageId: '',
    ghlApiVersion: '2021-07-28',
    ghlSource: 'Pipsqueak Lead Magnet',
    ghlCustomFieldKey: '',
  });
  
  const [errors, setErrors] = useState({});
  const [showGhlToken, setShowGhlToken] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [pipelineError, setPipelineError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { isDarkMode } = useTheme();

  const token = localStorage.getItem('token');

  const validateConfig = useCallback((configToValidate) => {
    const newErrors = {};
    
    if (configToValidate.ghlEnabled) {
      if (!configToValidate.ghlLocationId?.trim()) {
        newErrors.ghlLocationId = 'Location ID is required when GoHighLevel is enabled';
      }
      if (!configToValidate.ghlApiToken?.trim()) {
        newErrors.ghlApiToken = 'API Token is required when GoHighLevel is enabled';
      }
      if (configToValidate.ghlPipelineId && !configToValidate.ghlPipelineStageId) {
        newErrors.ghlPipelineStageId = 'Pipeline Stage ID is required when Pipeline ID is provided';
      }
      if (!configToValidate.ghlPipelineId && configToValidate.ghlPipelineStageId) {
        newErrors.ghlPipelineId = 'Pipeline ID is required when Pipeline Stage ID is provided';
      }
    }
    
    return newErrors;
  }, []);

  const updateConfig = useCallback((updates) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      setErrors(validateConfig(newConfig));
      return newConfig;
    });
  }, [validateConfig]);

  // Load settings from backend
  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('s2/api/ghl/config', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.configured) {
        setConfig({
          ghlEnabled: data.enabled || false,
          ghlApiToken: data.api_token || '', 
          ghlLocationId: data.location_id || '',
          ghlPipelineId: data.pipeline_id || '',
          ghlPipelineStageId: data.pipeline_stage_id || '',
          ghlApiVersion: data.api_version || '2021-07-28',
          ghlSource: data.source || 'Pipsqueak Lead Magnet',
          ghlCustomFieldKey: data.custom_field_key || '',
        });
        
      
      } else {
        // No existing config, use defaults
        console.log('No existing GHL configuration found');
      }
    } catch (error) {
      console.error('Failed to load GHL settings:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Save settings to backend
  const saveSettings = useCallback(async () => {
    const validationErrors = validateConfig(config);
    if (Object.keys(validationErrors).length > 0) {
      alert(`Please fix the following errors:\n${Object.keys(validationErrors).join('\n')}`);
      return false;
    }
    
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      // Don't send masked token
      let tokenToSend = config.ghlApiToken;
      if (tokenToSend === '••••••••') {
        tokenToSend = ''; // Will use existing stored token
      }
      
      const response = await fetch('s2/api/ghl/integrate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ghl_api_token: tokenToSend,
          ghl_location_id: config.ghlLocationId,
          ghl_pipeline_id: config.ghlPipelineId || null,
          ghl_pipeline_stage_id: config.ghlPipelineStageId || null,
          ghl_api_version: config.ghlApiVersion,
          ghl_source: config.ghlSource,
          ghl_enabled: config.ghlEnabled,
          ghl_custom_field_key: config.ghlCustomFieldKey || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        
        // Dispatch event for other components
        const event = new CustomEvent('integrationSettingsChanged', { 
          detail: { config, errors: validateConfig(config) }
        });
        window.dispatchEvent(event);
        return true;
      } else {
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save GHL settings:', error);
      alert(`Failed to save settings: ${error.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [config, validateConfig, token]);

  const fetchPipelines = async () => {
    if (!config.ghlApiToken || !config.ghlLocationId) {
      setPipelineError("Please enter API Token and Location ID first");
      return;
    }
    
    setLoadingPipelines(true);
    setPipelineError(null);
    try {
      // Fix: Remove /s2 prefix to match backend
      const response = await fetch('s2/api/ghl/pipelines', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          api_token: config.ghlApiToken === '••••••••' ? '' : config.ghlApiToken,
          location_id: config.ghlLocationId,
          api_version: config.ghlApiVersion
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        setPipelines(data.pipelines || []);
        if (data.pipelines?.length === 0) {
          setPipelineError('No pipelines found for this location');
        }
      } else {
        setPipelineError(data.error || 'Failed to fetch pipelines');
      }
    } catch (err) {
      setPipelineError('Failed to fetch pipelines');
      console.error(err);
    } finally {
      setLoadingPipelines(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Handle save from parent components
  useEffect(() => {
    const handleSaveRequest = (event) => {
      if (event.detail?.type === 'saveIntegration') {
        saveSettings();
      }
    };
    
    window.addEventListener('requestIntegrationSave', handleSaveRequest);
    return () => window.removeEventListener('requestIntegrationSave', handleSaveRequest);
  }, [saveSettings]);

  // Expose config to parent via global variable
  useEffect(() => {
    window.__integrationConfig = config;
    window.__integrationErrors = errors;
  }, [config, errors]);

  if (loading) {
    return (
      <div className="integration-settings">
        <div className="config-section" style={{ textAlign: 'center', padding: '3rem' }}>
          <div>Loading integration settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="integration-settings">
      <div className="config-section ghl-section">
        <div className="ghl-header">
          <div className="ghl-title-group">
            <div className="ghl-logo-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h4 className={`ghl-title ${isDarkMode ? 'text-light' : ''}`}>GoHighLevel Integration</h4>
              <p className={`ghl-subtitle ${isDarkMode ? 'text-light' : ''}`}>Automatically sync leads as contacts and create opportunities</p>
            </div>
          </div>
          <button
            type="button"
            className={`ghl-toggle ${config.ghlEnabled ? 'active' : ''}`}
            onClick={() => updateConfig({ ghlEnabled: !config.ghlEnabled })}
            aria-label="Toggle GoHighLevel integration"
          >
            {config.ghlEnabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
            <span className="ghl-toggle-label">{config.ghlEnabled ? 'Active' : 'Inactive'}</span>
          </button>
        </div>

        {config.ghlEnabled && (
          <div className="ghl-fields">
            <div className={`ghl-status-bar ${isDarkMode ? 'bg-dark' : ''}`}>
              <div className={`ghl-status-dot ${config.ghlApiToken && config.ghlLocationId ? 'connected' : 'disconnected'}`} />
              <span className={`ghl-status-text ${isDarkMode ? 'text-light' : ''}`}>
                {config.ghlApiToken && config.ghlLocationId 
                  ? 'Ready to sync — contacts will be upserted on lead capture' 
                  : 'Fill in the required fields below to activate'
                }
              </span>
            </div>

            <div className="config-grid" style={{ gap: '2rem' }}>
              <div className="form-group full-width">
                <label className="form-label">
                  <Lock size={16} />
                  API Access Token *
                </label>
                <div className="ghl-token-input-group">
                  <input
                    type={showGhlToken ? 'text' : 'password'}
                    value={config.ghlApiToken}
                    onChange={(e) => updateConfig({ ghlApiToken: e.target.value })}
                    className={`form-input ${errors.ghlApiToken ? 'input-error' : ''} ${isDarkMode ? 'bg-dark text-gray border-secondary' : ''}`}
                    placeholder="Enter your GoHighLevel API token"
                    style={{ margin: '0' }}
                  />
                  <button
                    type="button"
                    className="ghl-token-toggle"
                    onClick={() => setShowGhlToken(!showGhlToken)}
                  >
                    {showGhlToken ? <EyeOff size={16} /> : <EyeIcon size={16} />}
                  </button>
                </div>
                {errors.ghlApiToken && (
                  <div className="error-message">{errors.ghlApiToken}</div>
                )}
                <div className="input-hint">
                  <strong>How to get it:</strong> Go to GHL Settings → Private integrations → Create New Integration. 
                  Select 'contacts.readonly' and 'contacts.write', plus 'opportunities.readonly' and
                  'opportunities.write' to create opportunities in a pipeline.
                </div>
              </div>

              <div className="form-group mb-8">
                <label className="form-label">
                  <Globe size={16} />
                  Location ID *
                </label>
                <input
                  type="text"
                  value={config.ghlLocationId}
                  onChange={(e) => updateConfig({ ghlLocationId: e.target.value })}
                  className={`form-input ${errors.ghlLocationId ? 'input-error' : ''} ${isDarkMode ? 'bg-dark text-gray border-secondary' : ''}`}
                  placeholder="e.g. ve9EPM428h8vShlRW1KT"
                />
                {errors.ghlLocationId && (
                  <div className="error-message">{errors.ghlLocationId}</div>
                )}
                <div className="input-hint">
                  Find your Location ID in GoHighLevel under Settings → Business Profile
                </div>
              </div>

              <div className="form-group mb-8">
                <label className="form-label">
                  <Settings size={16} />
                  API Version
                </label>
                <select
                  value={config.ghlApiVersion}
                  onChange={(e) => updateConfig({ ghlApiVersion: e.target.value })}
                  className={`form-select ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                >
                  <option value="2021-07-28">2021-07-28</option>
                </select>
                <div className="input-hint">
                  Header parameter required by GoHighLevel API
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
              <label className="form-label">
                📝 Scanned Domain Custom Field Key (Optional)
              </label>
              <input
                type="text"
                value={config.ghlCustomFieldKey || ''}
                onChange={(e) => updateConfig({ ghlCustomFieldKey: e.target.value })}
                className={`form-input ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                placeholder="e.g. scanned_domain_report"
              />
              <div className="input-hint">
                <strong>To save the scanned domain link in GHL:</strong><br/>
                1. Go to <strong>Contacts</strong> → <strong>Manage fields</strong> → <strong>Add Custom field</strong><br/>
                2. Use <strong>Text Input single line</strong><br/>
                3. Enter a Name, select <strong>Contact</strong> for the Object<br/>
                4. Save and enter the generated <strong>Key</strong> here
              </div>
            </div>

            {/* Opportunities Section */}
            <div className="ghl-header border-t" style={{ 
              paddingTop: '3rem', 
              borderColor: 'rgba(0,0,0,0.08)',
              marginBottom: '2rem' 
            }}>
              <div className="ghl-title-group">
                <div className="ghl-logo-badge" style={{ 
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'
                }}>
                  <span style={{ fontSize: '24px' }}>🎯</span>
                </div>
                <div>
                  <h4 className={`ghl-title ${isDarkMode ? 'text-light' : ''}`} style={{ fontSize: '1.25rem', fontWeight: '700' }}>Upsert Opportunities</h4>
                  <p className={`ghl-subtitle ${isDarkMode ? 'text-light' : ''}`}>
                    Automatically create an opportunity in your GoHighLevel pipeline
                  </p>
                </div>
              </div>
            </div>

            <div className="ghl-status-bar" style={{ 
              marginTop: '2rem', 
              marginBottom: '3rem', 
              padding: '1.25rem',
              backgroundColor: 'rgba(99, 102, 241, 0.03)',
              border: '1px solid rgba(99, 102, 241, 0.1)'
            }}>
              <div className={`ghl-status-dot ${config.ghlPipelineId && config.ghlPipelineStageId ? 'connected' : 'disconnected'}`} />
              <span className={`ghl-status-text ${isDarkMode ? 'text-light' : ''}`}>
                {config.ghlPipelineId && config.ghlPipelineStageId 
                  ? 'Pipeline sync active — opportunities will be created for each lead' 
                  : 'Pipeline sync inactive — fill in both fields below to activate'
                }
              </span>
            </div>

            <div className="input-hint" style={{ marginBottom: '1.5rem' }}>
              <strong>Your token needs the opportunities permissions:</strong>
              <ol style={{ margin: '0.25rem 0 0', paddingLeft: '1.125rem' }}>
                <li>In GHL, open the same sub-account and go to Settings → Private integrations</li>
                <li>Open your Pipsqueak integration and also select 'opportunities.readonly' and 'opportunities.write'</li>
                <li>Save. If GHL shows a new token, paste it into API Access Token above</li>
                <li>Click Fetch Pipelines &amp; Stages, pick a pipeline and stage, then save</li>
              </ol>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <button
                type="button"
                onClick={fetchPipelines}
                className="btn btn-secondary"
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  cursor: loadingPipelines || !config.ghlApiToken || !config.ghlLocationId ? 'not-allowed' : 'pointer',
                  opacity: loadingPipelines || !config.ghlApiToken || !config.ghlLocationId ? 0.6 : 1
                }}
                disabled={loadingPipelines || !config.ghlApiToken || !config.ghlLocationId}
              >
                {loadingPipelines ? 'Fetching...' : 'Fetch Pipelines & Stages'}
              </button>
              {pipelineError && (
                <div className="text-danger mt-2" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  <AlertTriangle size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  {pipelineError}
                </div>
              )}
            </div>

            <div className="config-grid" style={{ gap: '2rem' }}>
              <div className="form-group">
                <label className="form-label">
                  🔄 Pipeline
                </label>
                <select
                  value={config.ghlPipelineId}
                  onChange={(e) => {
                    updateConfig({ 
                      ghlPipelineId: e.target.value,
                      ghlPipelineStageId: ''
                    });
                  }}
                  className={`form-select ${errors.ghlPipelineId ? 'input-error' : ''} ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                >
                  <option  value="">Select a Pipeline</option>
                  {pipelines.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {errors.ghlPipelineId && (
                  <div className="error-message">{errors.ghlPipelineId}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  📌 Pipeline Stage
                </label>
                <select
                  value={config.ghlPipelineStageId}
                  onChange={(e) => updateConfig({ ghlPipelineStageId: e.target.value })}
                  className={`form-select ${errors.ghlPipelineStageId ? 'input-error' : ''} ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  disabled={!config.ghlPipelineId}
                >
                  <option value="">Select a Stage</option>
                  {config.ghlPipelineId && pipelines.find(p => p.id === config.ghlPipelineId)?.stages?.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {errors.ghlPipelineStageId && (
                  <div className="error-message">{errors.ghlPipelineStageId}</div>
                )}
              </div>
            </div>

            <div className={`ghl-info-box ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`} style={{ marginTop: '2rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px' }}>
              <Shield size={16} color="#10b981" />
              <strong>How it works:</strong> When a lead unlocks their report, their contact information 
              is automatically synchronized with GoHighLevel.
            </div>

            <div style={{ marginTop: '2rem', textAlign: 'right' }}>
              <button
                type="button"
                onClick={saveSettings}
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  background: saveSuccess ? '#10b981' : 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saveSuccess ? <CheckCircle size={16} /> : <Save size={16} />}
                {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Integration Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Integration;